import React from 'react';
import { defineMessages, FormattedMessage, FormattedNumber, type MessageValue } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { BodyText, ProductCardV2, theme } from '@sg/garnish';
import { Upsell } from '@sg/shared-fe-features-menu';
import { useLineItemModifierIngredients } from '@sg/shared-fe-features-ordering';

import { useLineItemDescription, useLineItemName } from '@order/shared-hooks';

import type { PartialLineItem } from '../../../../graphql';

// ─────────────────────────────────────────────────────────────────────────────

export const UpsellLineItemCard = (props: LineItemCardProps) => {
  const { lineItem } = props;

  const { product, quantity, perItemCost } = lineItem;
  const calories = product?.calories;
  const lineItemImageUrl = product?.asset?.url;

  const lineItemName = useLineItemName(product, lineItem);
  const description = useLineItemDescription(lineItem);

  const showLabels = !product.isCustom;

  const { addedIngredientsText, removedIngredientsText } = useLineItemModifierIngredients({
    selectedModifiers: lineItem.selectedModifiers,
    showLabels,
    product,
  });

  const hasModdedIngredients = Boolean(addedIngredientsText) || Boolean(removedIngredientsText);

  return (
    <Upsell.LineItemContainer>
      <Upsell.LineItemImage imageUrl={lineItemImageUrl || ''} lineItemName={lineItemName} />

      <Upsell.LineItemHeader lineItemName={lineItemName} quantity={quantity || 1} />

      <ProductCardV2.ProductDetailsContainer>
        {perItemCost ? (
          <ProductCardV2.ProductDetail sizeMatch={['18']} palette="dark-kale">
            <FormattedNumber
              value={perItemCost / 100}
              // eslint-disable-next-line react/style-prop-object
              style="currency"
              currency="USD"
            />
          </ProductCardV2.ProductDetail>
        ) : null}

        {calories !== undefined && calories !== null && (
          <ProductCardV2.ProductDetail sizeMatch={['18']}>
            <FormattedMessage {...messages.caloriesText} values={{ calories }} />
          </ProductCardV2.ProductDetail>
        )}
      </ProductCardV2.ProductDetailsContainer>

      {hasModdedIngredients ? (
        <View style={styles.ingredientListWrapper}>
          <View>
            {addedIngredientsText ? (
              <BodyText sizeMatch={['16']}>{addedIngredientsText}</BodyText>
            ) : null}

            {removedIngredientsText ? (
              <BodyText sizeMatch={['16']}>{removedIngredientsText}</BodyText>
            ) : null}
          </View>
        </View>
      ) : null}

      <Upsell.LineItemDescription description={description} />
    </Upsell.LineItemContainer>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{ readonly caloriesText: { readonly calories: MessageValue } }>({
  caloriesText: {
    defaultMessage: '{calories} CALS',
    description: 'Upsell | Line item | Calories',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  ingredientListWrapper: {
    paddingTop: theme.spacing['3'],
  },
});

// ─────────────────────────────────────────────────────────────────────────────

type LineItemCardProps = {
  lineItem: PartialLineItem;
};
